import { logger } from '@adornis/base/logging.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Subscription } from '@adornis/baseql/decorators.js';
import { constructValue } from '@adornis/baseql/entities/construct.js';
import { getCollectionHandle, getRawCollection } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import type { Maybe } from '@graphql-tools/utils';
import { DateTime } from 'luxon';
import type { Observable } from 'rxjs';
import { catchError, from, mapTo, of, switchMap } from 'rxjs';
import { NotificationSeenEventlog } from '../eventlogs/NotificationSeenEventlog.js';
import { CampusNotificationFilter } from '../filter/CampusNotificationFilter.js';
import { ChancenNotification } from './ChancenNotification.js';

/**
 * @entity CampusNotification
 *
 */
@Entity()
export class CampusNotification extends ChancenNotification {
  public static override _class = 'CampusNotification';

  @Field(type => Boolean, { default: v => v ?? false })
  public forEmployees: Maybe<boolean>;

  @Field(type => [Int], { default: v => v ?? [] })
  public forGrades: Maybe<number[]>;

  @Field(type => [ID], { default: v => v ?? [] })
  public forGroupIDs: Maybe<string[]>;

  @Field(type => [ID], { default: v => v ?? [] })
  public forSchoolIDs: Maybe<string[]>;

  @Subscription(type => [CampusNotification])
  static subscribeNotificationsByFilterPaginated(
    @Arg('skip', type => Int) skip: number,
    @Arg('limit', type => Int) limit: number,
    @Arg('filter', type => CampusNotificationFilter) filter: CampusNotificationFilter,
  ) {
    return (gqlFields: BaseQLSelectionSet<CampusNotification>): Observable<CampusNotification[]> => {
      if (!filter) return of([] as CampusNotification[]);

      const selector = filter.getMongoFilter();
      const handle = getCollectionHandle<CampusNotification>(this._collectionName);
      return from(publishFilter(this._class)).pipe(
        switchMap(publishFilter => {
          return handle
            .watchQuery(publishFilter ? { $and: [publishFilter, selector] } : selector, {
              ...filter.getMongoOptions().sort,
              skip,
              limit,
            })
            .pipe(mapTo(selector));
        }),
        switchMap(async docs => {
          const collection = await getRawCollection<CampusNotification>(CampusNotification._collectionName);
          const agg = await collection
            .aggregate([
              {
                $match: docs,
              },
              {
                $lookup: {
                  from: NotificationSeenEventlog._collectionName,
                  let: { notificationID: '$_id' },
                  pipeline: [
                    {
                      $match: {
                        $expr: { $eq: ['$notificationID', '$$notificationID'] },
                        $and: [
                          {
                            userID: filter.userID,
                          },
                        ],
                      },
                    },
                  ],
                  as: 'eventlogs',
                },
              },

              {
                $match: {
                  $or: [
                    filter.noTimeLimit ? {} : { eventlogs: { $size: 0 } },
                    filter.noTimeLimit
                      ? {}
                      : { 'eventlogs.createdAt': { $gte: DateTime.now().startOf('day').toJSDate() } },
                  ],
                },
              },
              { $skip: skip },
              ...(limit ? [{ $limit: limit }] : []),
            ])
            .toArray();
          return agg.map(x => constructValue(x) as CampusNotification);
        }),

        catchError(err => {
          logger.error(err);
          return of([] as CampusNotification[]);
        }),
      );
    };
  }
}
